import React, { useState } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  DateInput,
  required,
  useRecordContext,
  useNotify,
  useUpdate,
  useGetList,
  useInput,
} from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  MenuItem,
  Chip,
  Stack,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as CheckIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Equipment type choices based on schema enum
const equipmentTypes = [
  { id: 'CAR', name: 'Car' },
  { id: 'VAN', name: 'Van' },
  { id: 'TRUCK', name: 'Truck' },
  { id: 'TRACTOR', name: 'Tractor' },
  { id: 'TRAILER', name: 'Trailer' },
  { id: 'BUS', name: 'Bus' },
  { id: 'CONSTRUCTION', name: 'Construction Equipment' },
  { id: 'FORKLIFT', name: 'Forklift' },
  { id: 'CRANE', name: 'Crane' },
  { id: 'EXCAVATOR', name: 'Excavator' },
  { id: 'LOADER', name: 'Loader' },
  { id: 'BULLDOZER', name: 'Bulldozer' },
  { id: 'GRADER', name: 'Grader' },
  { id: 'COMPACTOR', name: 'Compactor' },
  { id: 'BACKHOE', name: 'Backhoe' },
  { id: 'SKID_STEER', name: 'Skid Steer' },
  { id: 'DUMP_TRUCK', name: 'Dump Truck' },
  { id: 'CONCRETE_MIXER', name: 'Concrete Mixer' },
  { id: 'PUMP_TRUCK', name: 'Pump Truck' },
  { id: 'TANKER', name: 'Tanker' },
  { id: 'REFRIGERATED_TRUCK', name: 'Refrigerated Truck' },
  { id: 'BOX_TRUCK', name: 'Box Truck' },
  { id: 'PICKUP_TRUCK', name: 'Pickup Truck' },
  { id: 'SEMI_TRUCK', name: 'Semi Truck' },
  { id: 'GARBAGE_TRUCK', name: 'Garbage Truck' },
  { id: 'TOW_TRUCK', name: 'Tow Truck' },
  { id: 'DELIVERY_VAN', name: 'Delivery Van' },
  { id: 'FLATBED', name: 'Flatbed' },
  { id: 'OTHER', name: 'Other' },
];

const transmissionTypes = [
  { id: 'MANUAL', name: 'Manual' },
  { id: 'AUTOMATIC', name: 'Automatic' },
];

const ownershipTypes = [
  { id: 'OWNED', name: 'Owned' },
  { id: 'LEASED', name: 'Leased' },
];

const statusChoices = [
  { id: true, name: 'Active' },
  { id: false, name: 'Inactive' },
];

interface EquipmentFormProps {
  onSubmit?: (data: any) => void;
  defaultValues?: any;
  transform?: (data: any) => any;
}

interface MultiSelectFieldProps {
  source: string;
  reference: string;
  label: string;
  optionText: string;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  source,
  reference,
  label,
  optionText,
}) => {
  const {
    field,
    fieldState: { error },
  } = useInput({ source });

  // Load available items from API
  const { data: availableItems = [], isLoading } = useGetList(reference, {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  const selectedIds = field.value || [];

  const handleAdd = (itemId: number) => {
    if (!selectedIds.includes(itemId)) {
      const newSelection = [...selectedIds, itemId];
      field.onChange(newSelection);
    }
  };

  const handleRemove = (itemId: number) => {
    const newSelection = selectedIds.filter((id: number) => id !== itemId);
    field.onChange(newSelection);
  };

  const selectedItems = selectedIds
    .map((id: number) => availableItems.find(item => item.id === id))
    .filter(Boolean);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>

      {/* Selected items display */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {selectedItems.map((item: any) => (
          <Chip
            key={item.id}
            label={item[optionText]}
            onDelete={() => handleRemove(item.id)}
            color="primary"
            variant="outlined"
            sx={{ mb: 1 }}
          />
        ))}
        {selectedItems.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No {label.toLowerCase()} selected
          </Typography>
        )}
      </Stack>

      {/* Add new dropdown */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          fullWidth
          placeholder={`Select ${label}...`}
          onChange={e => {
            const value = Number(e.target.value);
            if (value) {
              handleAdd(value);
            }
          }}
          value=""
          sx={{ maxWidth: 300 }}
          disabled={isLoading}
        >
          <MenuItem value="" disabled>
            {isLoading ? 'Loading...' : `Select ${label}...`}
          </MenuItem>
          {availableItems
            .filter(item => !selectedIds.includes(item.id))
            .map(item => (
              <MenuItem key={item.id} value={item.id}>
                {item[optionText]}
              </MenuItem>
            ))}
        </TextField>
      </Box>

      {error && (
        <Typography variant="caption" color="error">
          {error.message}
        </Typography>
      )}
    </Box>
  );
};

// File Upload Component based on fortress-web patterns
interface FileUploadProps {
  label: string;
  accept?: string;
  multiple?: boolean;
  helperText?: string;
  source: string;
  onFilesChange?: (files: File[]) => void;
  folderPath?: string;
  maxSizeMB?: number;
  equipmentId?: string | number;
}

interface UploadedFile {
  file: File;
  progress: number;
  uploaded: boolean;
  error?: string;
  url?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept = 'image/*,.pdf',
  multiple = false,
  helperText,
  source,
  onFilesChange,
  folderPath = 'equipment-documents',
  maxSizeMB = 30,
  equipmentId,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [currentFile, setCurrentFile] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const notify = useNotify();
  const [update] = useUpdate();
  const record = useRecordContext();

  // Fetch current file if exists
  React.useEffect(() => {
    const fetchCurrentFile = async () => {
      if (!equipmentId) {
        return;
      }

      const fieldName =
        source === 'ownershipPermitFiles' ? 'ownershipPermitFileId' : 'weightRatingFileId';
      const fileId = record?.[fieldName];

      if (fileId) {
        try {
          const response = await fetch(
            `${import.meta.env.VITE_SIMPLE_REST_URL}/files/${fileId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
              },
            }
          );

          if (response.ok) {
            const fileData = await response.json();
            setCurrentFile(fileData);
          }
        } catch (error) {
          console.error('Error fetching current file:', error);
        }
      }
    };

    fetchCurrentFile();
  }, [equipmentId, source, record]);

  const validateFile = (file: File): string | null => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `File size must be less than ${maxSizeMB}MB`;
    }
    return null;
  };

  const uploadFileToServer = async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('description', `${label} - ${file.name}`);
    formData.append('folderPath', folderPath);

    const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
    const accessToken = localStorage.getItem('accessToken');

    const response = await fetch(`${baseUrl}/files`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return response.json();
  };

  const updateEquipmentWithFile = async (fileData: any, fieldName: string) => {
    if (!equipmentId) {
      return;
    }

    try {
      // Get current equipment data
      const currentEquipment = await fetch(
        `${
          import.meta.env.VITE_SIMPLE_REST_URL
        }/equipment/${equipmentId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      ).then(res => res.json());

      // Update the specific file field
      const updateData = {
        ...currentEquipment,
        [fieldName]: fileData.id || fileData.url || fileData.path,
      };

      await update('equipment', {
        id: equipmentId,
        data: updateData,
        previousData: currentEquipment,
      });

      notify(`${label} saved to equipment record`, { type: 'success' });
    } catch (error: any) {
      notify(`Failed to save ${label} to equipment: ${error.message}`, { type: 'error' });
    }
  };

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);

    // Validate files
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        notify(error, { type: 'error' });
        return;
      }
    }

    setUploading(true);

    const newUploadedFiles: UploadedFile[] = fileArray.map(file => ({
      file,
      progress: 0,
      uploaded: false,
    }));

    setUploadedFiles(prev => [...prev, ...newUploadedFiles]);

    // Upload files one by one with real progress
    for (let i = 0; i < newUploadedFiles.length; i++) {
      const uploadFileItem = newUploadedFiles[i];

      try {
        // Start progress tracking
        const progressInterval = setInterval(() => {
          setUploadedFiles(prev =>
            prev.map(f =>
              f.file === uploadFileItem.file ? { ...f, progress: Math.min(f.progress + 5, 80) } : f
            )
          );
        }, 100);

        // Actual upload to file server
        const result = await uploadFileToServer(uploadFileItem.file);

        clearInterval(progressInterval);

        // Update progress to 90% while saving to equipment
        setUploadedFiles(prev =>
          prev.map(f => (f.file === uploadFileItem.file ? { ...f, progress: 90 } : f))
        );

        // Determine field name based on source
        const fieldName =
          source === 'ownershipPermitFiles' ? 'ownershipPermitFileId' : 'weightRatingFileId';

        // Save file reference to equipment record
        await updateEquipmentWithFile(result, fieldName);

        // Mark as fully complete
        setUploadedFiles(prev =>
          prev.map(f =>
            f.file === uploadFileItem.file
              ? { ...f, progress: 100, uploaded: true, url: result.url || result.path }
              : f
          )
        );

        // Set as current file and clear uploaded files (since we only allow one file)
        setCurrentFile(result);
        setUploadedFiles([]);

        notify(`${uploadFileItem.file.name} uploaded and saved successfully`, { type: 'success' });
      } catch (error: any) {
        setUploadedFiles(prev =>
          prev.map(f =>
            f.file === uploadFileItem.file ? { ...f, error: error.message, progress: 0 } : f
          )
        );
        notify(`Failed to upload ${uploadFileItem.file.name}: ${error.message}`, { type: 'error' });
      }
    }

    setUploading(false);

    // Update parent with all files
    const allFiles = [...uploadedFiles.map(f => f.file), ...fileArray];
    onFilesChange?.(allFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleDeleteCurrentFile = async () => {
    if (!currentFile || !equipmentId) {
      return;
    }

    try {
      const fieldName =
        source === 'ownershipPermitFiles' ? 'ownershipPermitFileId' : 'weightRatingFileId';

      await update('equipment', {
        id: equipmentId,
        data: { [fieldName]: null },
        previousData: record,
      });

      setCurrentFile(null);
      notify('File deleted successfully', { type: 'success' });
    } catch (error: any) {
      notify(`Failed to delete file: ${error.message}`, { type: 'error' });
    }
  };

  const isImage = (filename: string) => {
    return /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(filename);
  };

  const handleViewFile = () => {
    setViewDialogOpen(true);
  };

  const handleDownload = () => {
    if (currentFile?.url) {
      window.open(currentFile.url, '_blank');
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      {helperText && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {helperText}
        </Typography>
      )}

      {/* Show current file if exists */}
      {currentFile ? (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
          {/* File Preview */}
          <Box
            sx={{
              height: 200,
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#f0f0f0' },
            }}
            onClick={handleViewFile}
          >
            {isImage(currentFile.name) ? (
              <img
                src={currentFile.url}
                alt={currentFile.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <FileIcon sx={{ fontSize: 64, color: '#1976d2', mb: 1 }} />
                <Typography variant="body2">{currentFile.name}</Typography>
              </Box>
            )}
          </Box>

          {/* File Info & Actions */}
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" noWrap title={currentFile.name}>
              {currentFile.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {currentFile.size
                ? `${(currentFile.size / 1024 / 1024).toFixed(2)} MB`
                : 'Unknown size'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" startIcon={<ViewIcon />} onClick={handleViewFile}>
                View
              </Button>
              <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}>
                Download
              </Button>
              <Button size="small" startIcon={<UploadIcon />} onClick={() => setCurrentFile(null)}>
                Replace
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteCurrentFile}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          {/* Drop Zone - only show if no current file */}
          <Card
            sx={{
              border: `2px dashed ${isDragOver ? '#1976d2' : '#e0e0e0'}`,
              backgroundColor: isDragOver ? '#f3f9ff' : '#fafafa',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#1976d2',
                backgroundColor: '#f3f9ff',
              },
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <input
                type="file"
                accept={accept}
                multiple={multiple}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}
                disabled={uploading}
              />
              <label htmlFor={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}>
                {uploading ? (
                  <CircularProgress sx={{ fontSize: 48, mb: 2 }} />
                ) : (
                  <UploadIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
                )}
                <Typography variant="h6" gutterBottom>
                  {uploading ? 'Uploading...' : 'Drag and drop files here'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {uploading ? 'Please wait...' : 'or click to browse files'}
                </Typography>
                <Button variant="outlined" component="span" disabled={uploading}>
                  Choose Files
                </Button>
              </label>
            </CardContent>
          </Card>

          {/* File List with Progress - only during upload */}
          {uploadedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Uploading:
              </Typography>
              <List dense>
                {uploadedFiles.map((uploadedFile, index) => (
                  <ListItem
                    key={index}
                    sx={{ px: 0, flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <FileIcon
                        sx={{ mr: 1, color: uploadedFile.uploaded ? '#4caf50' : '#1976d2' }}
                      />
                      <ListItemText
                        primary={uploadedFile.file.name}
                        secondary={`${(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB`}
                      />
                      {uploadedFile.uploaded && <CheckIcon sx={{ color: '#4caf50', mr: 1 }} />}
                    </Box>
                    {uploadedFile.progress > 0 && uploadedFile.progress < 100 && (
                      <LinearProgress
                        variant="determinate"
                        value={uploadedFile.progress}
                        sx={{ mt: 1, width: '100%' }}
                      />
                    )}
                    {uploadedFile.error && (
                      <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {uploadedFile.error}
                      </Typography>
                    )}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </>
      )}

      {/* View Dialog */}
      {currentFile && (
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {currentFile.name}
              <IconButton onClick={() => setViewDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {isImage(currentFile.name) ? (
              <img
                src={currentFile.url}
                alt={currentFile.name}
                style={{ width: '100%', height: 'auto' }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <FileIcon sx={{ fontSize: 96, color: '#1976d2', mb: 2 }} />
                <Typography variant="h6">{currentFile.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This file type cannot be previewed. Click download to view the file.
                </Typography>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                  Download File
                </Button>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export const EquipmentForm: React.FC<EquipmentFormProps> = ({
  onSubmit,
  defaultValues = { active: true },
  transform,
}) => {
  const [ownershipPermitFiles, setOwnershipPermitFiles] = useState<File[]>([]);
  const [weightRatingFiles, setWeightRatingFiles] = useState<File[]>([]);
  const record = useRecordContext(); // Get current record for edit mode

  // Transform existing record data for form fields
  const getTransformedDefaultValues = () => {
    const transformed = {
      active: true,
      ...defaultValues,
    };

    // If we have a record (edit mode), transform the utilizations and commodities
    if (record) {
      // Transform utilizations to utilizationIds (from junction table structure)
      if (record.utilizations && Array.isArray(record.utilizations)) {
        transformed.utilizationIds = record.utilizations.map(item =>
          item.utilization ? item.utilization.id : item.utilizationId || item.id
        );
      }

      // Transform commodities to commodityIds (from junction table structure)
      if (record.commodities && Array.isArray(record.commodities)) {
        transformed.commodityIds = record.commodities.map(item =>
          item.commodity ? item.commodity.id : item.commodityId || item.id
        );
      }
    }

    return transformed;
  };

  const formDefaultValues = getTransformedDefaultValues();

  const handleFormSubmit = onSubmit
    ? (data: any) => {
        // Add file uploads to form data
        const formDataWithFiles = {
          ...data,
          ownershipPermitFiles,
          weightRatingFiles,
        };

        if (transform) {
          return onSubmit(transform(formDataWithFiles));
        }
        return onSubmit(formDataWithFiles);
      }
    : undefined;

  const currentYear = new Date().getFullYear();
  const yearChoices = Array.from({ length: 50 }, (_, i) => ({
    id: currentYear - i,
    name: (currentYear - i).toString(),
  }));

  return (
    <SimpleForm onSubmit={handleFormSubmit} defaultValues={formDefaultValues}>
      <Box sx={{ width: '100%', maxWidth: 1000 }}>
        {/* Basic Information */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Basic Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextInput
                source="name"
                validate={[required()]}
                fullWidth
                label="Equipment Name"
                helperText="e.g., 2021 Nissan Sentra or Unit 123"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="type"
                choices={equipmentTypes}
                validate={[required()]}
                fullWidth
                label="Vehicle Type"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput
                source="maxHauledKg"
                fullWidth
                label="Maximum Weight Hauled (Kilograms)"
                helperText="Optional - Maximum weight this vehicle can haul"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Status */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Status
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SelectInput
                source="active"
                choices={statusChoices}
                fullWidth
                label="Vehicle Status"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Vehicle Details */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Vehicle Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <SelectInput
                source="manufacturedYear"
                choices={yearChoices}
                fullWidth
                label="Vehicle Year"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextInput
                source="make"
                fullWidth
                label="Vehicle Make"
                helperText="e.g., Nissan, Ford, Caterpillar"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextInput
                source="model"
                fullWidth
                label="Vehicle Model"
                helperText="e.g., Sentra, F-150, 320D"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="transmission"
                choices={transmissionTypes}
                fullWidth
                label="Transmission"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Identification */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Vehicle Identification
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextInput
                source="licensePlate"
                fullWidth
                label="License Plate Number"
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextInput
                source="vin"
                fullWidth
                label="Vehicle Identification Number (VIN)"
                helperText="Optional"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Ownership & Financial */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Ownership & Financial Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="ownershipType"
                choices={ownershipTypes}
                fullWidth
                label="Ownership Type"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <DateInput 
                source="acquireDate" 
                fullWidth 
                label="Vehicle Acquire Date" 
                parse={(value) => value ? new Date(value + 'T00:00:00').toISOString() : null}
                format={(value) => value ? value.split('T')[0] : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput
                source="listPriceNew"
                fullWidth
                label="List Price New"
                helperText="Total price of the vehicle and any associated equipment when new"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput
                source="actualCashValue"
                fullWidth
                label="Actual Cash Value"
                helperText="Current market value of the vehicle and any associated equipment"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Job Types (Utilizations) */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Job Type(s)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add categories that describe what type of work is performed with this vehicle.
          </Typography>
          <MultiSelectField
            source="utilizationIds"
            reference="vehicle-utilizations"
            label="Job Types"
            optionText="name"
          />
        </Paper>

        {/* Commodities */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Commodities Hauled
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add commodities that are hauled with the vehicle
          </Typography>
          <MultiSelectField
            source="commodityIds"
            reference="commodities"
            label="Commodities"
            optionText="name"
          />
        </Paper>

        {/* Notes */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Notes
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextInput source="notes" fullWidth multiline rows={4} label="Additional Notes" />
            </Grid>
          </Grid>
        </Paper>

        {/* Document Uploads */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
            Documents
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <FileUpload
                label="Ownership Permit"
                accept="image/*,.pdf"
                helperText="Upload a scan or image of your ownership permit (optional)"
                source="ownershipPermitFiles"
                onFilesChange={setOwnershipPermitFiles}
                equipmentId={record?.id}
                folderPath="equipment/ownership-permits"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FileUpload
                label="Weight Rating Certificate"
                accept="image/*,.pdf"
                helperText="Upload a scan or image of a weight rating certificate (optional)"
                source="weightRatingFiles"
                onFilesChange={setWeightRatingFiles}
                equipmentId={record?.id}
                folderPath="equipment/weight-certificates"
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </SimpleForm>
  );
};
